import {
  AnalysisHistoryRecord,
  AnalysisOptions,
  AnalysisResponse,
  AnalysisSet,
  ComparePlateListsRequest,
  ComponentDesign,
  ComponentDesignCreationInput,
  ComponentDesignSummariesAndTimestamps,
  ComponentDesignSummary,
  ComponentDesignTimestamps,
  ComponentDesignUploadFile,
  ComponentDesignWithInformation,
  ComponentDesignWithRecordedCanaries,
  ComponentDesignWithTimestamps,
  ComponentSide,
  ComponentTemplateType,
  ConnectingHardware,
  DimensionSet,
  DownloadMachineryTrsRequest,
  DownloadMaterialReportRequest,
  DownloadRequest,
  DownloadShopDrawingsRequest,
  DownloadTrussDAndERequest,
  DownloadTrussDesignDrawingsRequest,
  EndConditionSettings,
  Excavation,
  FeaturePatternTrussSettings,
  GableSettings,
  GenerateLoadCasesResult,
  JackConnectionsOnGirderWithGuids,
  JackConnectionsOnGirderWithNames,
  JackGirderConnection,
  JackInformation,
  LoadSet,
  Lumber,
  LumberWithStockLengthAndPrice,
  MaterialSelection,
  MaterialSettings,
  MemberAnalysisInfo,
  MemberAnalysisInputs,
  ParseFilesResult,
  PlateAnalysis,
  Point2D,
  ProfileTrussSettings,
  ReplaceLumberRequest,
  RoofContainer,
  SealingServiceComponentDesign,
  StubDistanceOption,
  Truss,
  UploadFilesResult,
  UsageData,
  WebbingInfo,
  Zone,
} from '@paragon/contract-library';

import { DesignService } from '../connections';
import { DownloadableFileResponse } from '../responses';

export function postUploadFiles(
  files: ComponentDesignUploadFile[],
  organization: string,
  materialSelection: MaterialSelection | null,
  projectGuidForUsageLogging?: string,
): Promise<UploadFilesResult> {
  return DesignService.post('componentDesigns/uploadFiles', {
    data: { files, organization, materialSelection },
    params: projectGuidForUsageLogging ? new URLSearchParams({ projectGuidForUsageLogging }) : undefined,
  });
}

export function postUploadIfcFile(file: ComponentDesignUploadFile, projectGuid: string): Promise<RoofContainer> {
  return DesignService.post('componentDesigns/uploadIfcFile', { data: { file, projectGuid } });
}

export function postParseFiles(
  files: ComponentDesignUploadFile[],
  organization: string,
  materialSelection: MaterialSelection | null,
): Promise<ParseFilesResult> {
  return DesignService.post('componentDesigns/parseFiles', { data: { files, organization, materialSelection } });
}

export function postParseJackConnectionsOnGirders(
  files: ComponentDesignUploadFile[],
): Promise<readonly JackConnectionsOnGirderWithNames[]> {
  return DesignService.post('componentDesigns/parseJackConnectionsOnGirders', { data: files });
}

export function postJackGirderConnections(jackConnectionsOnGirders: readonly JackConnectionsOnGirderWithGuids[]) {
  return DesignService.post('componentDesigns/jackGirderConnections', { data: jackConnectionsOnGirders });
}

interface ParseTrussSettingsResponse {
  readonly profileTrussSettings: readonly ProfileTrussSettings[];
  readonly featurePatternTrussSettings: readonly FeaturePatternTrussSettings[];
  readonly errors: readonly string[];
}

export function postParseTrussSettings(files: ComponentDesignUploadFile[]): Promise<ParseTrussSettingsResponse> {
  return DesignService.post('componentDesigns/parseTrussSettings', { data: files });
}

type InsertFromComponentDesignCreationInputResult =
  | {
      readonly result: 'Success';
      readonly componentDesignGuid: string;
    }
  | {
      readonly result: 'Exception';
      readonly isUserVisibleException: boolean;
      readonly exception: string;
    };

export function postFromComponentDesignComponentDesignCreationInput(
  componentDesignProcessorMessage: ComponentDesignCreationInput,
) {
  return DesignService.post<InsertFromComponentDesignCreationInputResult>(
    'componentDesigns/fromComponentDesignCreationInput',
    {
      data: componentDesignProcessorMessage,
    },
  );
}

export interface CostWeights {
  readonly perMember: number;
  readonly perUniqueMember: number;
  readonly perCut: number;
  readonly perJoint: number;
  readonly perSplice: number;
  readonly perBoardFoot: number;
  readonly perPlate: number;
  readonly twoPlyLabor: number;
  readonly threePlyLabor: number;
  readonly bracing: number;
}

export interface OptimizationCostBreakdown {
  readonly lumber: number;
  readonly plate: number;
  readonly boardFeet: number;
  readonly member: number;
  readonly uniqueMember: number;
  readonly cut: number;
  readonly joint: number;
  readonly splice: number;
  readonly plateLabor: number;
  readonly plyLabor: number;
  readonly bracing: number;
  readonly total: number;
}

export interface OptimizationCostComparison {
  readonly previousDesign: OptimizationCostBreakdown;
  readonly optimizedDesign: OptimizationCostBreakdown;
  readonly lumberOnly: boolean;
}

export interface OptimizerMissingLumberPrice {
  readonly lumber: Lumber;
  readonly stockLengthInInches: number;
  readonly defaultPricePerThousandBoardFeet: number | null;
}

export interface OptimizerMissingPlatePrice {
  readonly plateType: string;
  readonly defaultPricePerPound: number;
}

export interface OptimizerMissingPrices {
  readonly missingLumberPrices: readonly OptimizerMissingLumberPrice[];
  readonly missingPlatePrices?: readonly OptimizerMissingPlatePrice[];
}

export function getMissingOptimizerPrices(componentDesignGuid: string): Promise<OptimizerMissingPrices> {
  return DesignService.get(`componentDesigns/findMissingOptimizerLumberPrices/${componentDesignGuid}`);
}

interface UpgradeAndAnalyzeResponseBase {
  readonly result: 'SuccessfullyAnalyzed' | 'FailedBeforeFinishingAnalysis';
  readonly errorMessages: readonly string[];
  readonly excavations: readonly Excavation[];
  readonly timing: { readonly [key: string]: number };
  readonly loadSet: LoadSet | null;
  readonly updatedJackGirderConnections: readonly JackGirderConnection[];
}

interface UpgradeAndAnalyzeSuccessResponse extends UpgradeAndAnalyzeResponseBase {
  readonly result: 'SuccessfullyAnalyzed';
  readonly analysisSet: AnalysisSet;
  readonly analysisHistoryRecord: AnalysisHistoryRecord;
  readonly componentDesignTimestamps: ComponentDesignTimestamps;
  readonly trussDesignUsage: UsageData;
  readonly costComparison: OptimizationCostComparison | null;
}

interface UpgradeAndAnalyzeFailedResponse extends UpgradeAndAnalyzeResponseBase {
  readonly result: 'FailedBeforeFinishingAnalysis';
  readonly componentDesign: ComponentDesign | null;
  readonly componentDesignTimestamps: ComponentDesignTimestamps | null;
}

type UpgradeAndAnalyzeResponse = UpgradeAndAnalyzeSuccessResponse | UpgradeAndAnalyzeFailedResponse;

export function postUpgradeAndAnalyze(
  componentDesign: ComponentDesign,
  options: {
    analysisOptions: AnalysisOptions;
    recordCanaries: boolean;
    runPlateMislocations: boolean;
    runExhaustivePlateSearch: boolean;
    runOptimizations: boolean;
    allowOptimizationChordResizing: boolean;
    selectHangers: boolean;
    memberOnlyAnalysis: boolean;
    optimizePlating: boolean;
    maxOptimizationPlies: number | null;
    varySplicing: boolean;
    costWeights: CostWeights | null;
    userSpecifiedLumberPrices: readonly LumberWithStockLengthAndPrice[] | null;
    userSpecifiedPlatePrices: { readonly [plateType: string]: number } | null;
  },
): Promise<UpgradeAndAnalyzeResponse> {
  return DesignService.post(`componentDesigns/upgradeAndAnalyze`, {
    data: {
      componentDesign,
      analysisOptions: {
        abbreviated: options.analysisOptions.abbreviated ?? true,
        commitName: options.analysisOptions.commitName || null,
        logVariables: options.analysisOptions.logVariables || false,
      },
      recordCanaries: options.recordCanaries,
      runPlateMislocations: options.runPlateMislocations,
      runExhaustivePlateSearch: options.runExhaustivePlateSearch,
      runOptimizations: options.runOptimizations,
      allowOptimizationChordResizing: options.allowOptimizationChordResizing,
      selectHangers: options.selectHangers,
      memberOnlyAnalysis: options.memberOnlyAnalysis,
      optimizePlating: options.optimizePlating,
      maxOptimizationPlies: options.maxOptimizationPlies,
      varySplicing: options.varySplicing,
      costWeights: options.costWeights,
      userSpecifiedLumberPrices: options.userSpecifiedLumberPrices,
      userSpecifiedPlatePrices: options.userSpecifiedPlatePrices,
    },
  });
}
export type BackgroundOptimizationStatus = 'Pending' | 'InProgress' | 'Completed' | 'Failed';

interface OptimizationStartResponse {
  readonly operationGuid: string;
}

interface OptimizationOptions {
  readonly allowOptimizationChordResizing: boolean;
  readonly varySplicing: boolean;
  readonly optimizePlating: boolean;
  readonly runExhaustivePlateSearch: boolean;
  readonly maxOptimizationPlies: number | null;
  readonly costWeights: CostWeights;
  readonly userSpecifiedLumberPrices: readonly LumberWithStockLengthAndPrice[];
  readonly userSpecifiedPlatePrices: { readonly [plateType: string]: number };
}

export function startOptimization(
  componentDesign: ComponentDesign,
  options: OptimizationOptions,
): Promise<OptimizationStartResponse> {
  return DesignService.post(`componentDesigns/optimization`, {
    data: {
      componentDesign,
      allowOptimizationChordResizing: options.allowOptimizationChordResizing,
      varySplicing: options.varySplicing,
      optimizePlating: options.optimizePlating,
      runExhaustivePlateSearch: options.runExhaustivePlateSearch,
      maxOptimizationPlies: options.maxOptimizationPlies,
      costWeights: options.costWeights,
      userSpecifiedLumberPrices: options.userSpecifiedLumberPrices,
      userSpecifiedPlatePrices: options.userSpecifiedPlatePrices,
    },
  });
}

export interface BackgroundOptimizationOperation {
  readonly operationGuid: string;
  readonly organization: string;
  readonly createdAt: string;
  readonly status: BackgroundOptimizationStatus;
  readonly numCombinations: number | null;
  readonly currentIteration: number | null;
  readonly costComparison: OptimizationCostComparison | null;
  readonly truss: Truss | null;
}

export function getOptimizationOperationStatus(operationGuid: string): Promise<BackgroundOptimizationOperation> {
  return DesignService.get(`componentDesigns/optimization/${operationGuid}/status`);
}

export function postAnalyze(
  componentDesign: ComponentDesign,
  options: {
    analysisOptions: AnalysisOptions;
    recordCanaries: boolean;
    runPlateMislocations: boolean;
  },
): Promise<AnalysisResponse> {
  const params = new URLSearchParams();
  params.append('recordCanaries', options.recordCanaries.toString());
  params.append('runPlateMislocations', options.runPlateMislocations.toString());
  return DesignService.post(`componentDesigns/analyze`, {
    data: {
      componentDesign,
      options: {
        abbreviated: options.analysisOptions.abbreviated ?? true,
        commitName: options.analysisOptions.commitName || null,
        logVariables: options.analysisOptions.logVariables || false,
      },
    },
    params,
  });
}

export interface PlateAnalysisResult {
  readonly [plateGuid: string]: PlateAnalysis;
}

export function postAnalyzePlates(
  componentDesign: ComponentDesign,
  plateGuids: readonly string[],
  runPlateMislocations: boolean,
): Promise<PlateAnalysisResult> {
  return DesignService.post(`componentDesigns/analyzePlates`, {
    data: { componentDesign, plateGuids },
    params: new URLSearchParams({ runPlateMislocations: runPlateMislocations.toString() }),
  });
}

type AnalyzeByComponentDesignGuidResult =
  | {
      readonly result: 'Success';
      readonly analysisReportGuid: string;
    }
  | {
      readonly result: 'Exception';
      readonly isUserVisibleException: boolean;
      readonly exception: string;
    };

export function postAnalyzeByComponentDesignGuid(guid: string, options: AnalysisOptions = {}) {
  return DesignService.post<AnalyzeByComponentDesignGuidResult>(
    `componentDesigns/${guid}/analyzeByComponentDesignGuid`,
    { data: options },
  );
}

export function fetch(guid: string, sharedComponentSetLookupCode?: string): Promise<ComponentDesignWithInformation> {
  return DesignService.get(
    `componentDesigns/${guid}`,
    sharedComponentSetLookupCode ? { params: new URLSearchParams({ sharedComponentSetLookupCode }) } : undefined,
  );
}

export function bulkLoad(guids: string[]): Promise<ComponentDesign[]> {
  return DesignService.post('componentDesigns/bulkLoad', { data: guids });
}

export function bulkLoadTimestamps(guids: string[]): Promise<ComponentDesignTimestamps[]> {
  return DesignService.post('componentDesigns/bulkLoadTimestamps', { data: guids });
}

export function postGenerateAnalogModel(
  componentDesign: ComponentDesign,
  recordCanaries: boolean,
): Promise<ComponentDesignWithRecordedCanaries> {
  const params = new URLSearchParams();
  params.append('recordCanaries', recordCanaries.toString());
  return DesignService.post(`componentDesigns/generateAnalogModel`, {
    data: componentDesign,
    params,
  });
}

/**
 * This route will generate load cases but only save them and the ComponentDesign if there are no orphaned manual loads
 */
export function postGenerateLoadCases(
  componentDesign: ComponentDesign,
  recordCanaries: boolean,
): Promise<GenerateLoadCasesResult> {
  const params = new URLSearchParams();
  params.append('recordCanaries', recordCanaries.toString());
  return DesignService.post(`componentDesigns/generateLoadCases`, {
    data: componentDesign,
    params,
  });
}

export function postUpgradeMembers(
  componentDesign: ComponentDesign,
  recordCanaries: boolean,
): Promise<ComponentDesignWithRecordedCanaries> {
  const params = new URLSearchParams();
  params.append('recordCanaries', recordCanaries.toString());
  return DesignService.post(`componentDesigns/upgradeMembers`, { data: componentDesign, params });
}

export function postGeneratePlates(
  componentDesign: ComponentDesign,
  runExhaustivePlateSearch: boolean,
  recordCanaries: boolean,
): Promise<ComponentDesignWithRecordedCanaries> {
  const params = new URLSearchParams();
  params.append('runExhaustivePlateSearch', runExhaustivePlateSearch.toString());
  params.append('recordCanaries', recordCanaries.toString());
  return DesignService.post(`componentDesigns/generatePlates`, { data: componentDesign, params });
}

export function postGetJackInformation(componentDesignGuids: readonly string[]) {
  return DesignService.post<{ readonly [componentDesignGuid: string]: JackInformation | null }>(
    'componentDesigns/getJackInformation',
    { data: componentDesignGuids },
  );
}

export function downloadTrussDesignDrawings(downloadRequest: DownloadTrussDesignDrawingsRequest) {
  return DesignService.fetchFile('public/componentDesigns/download/trussDesignDrawings', {
    post: true,
    data: downloadRequest,
  });
}

type CreateTddInJobRequest = {
  jobId: number;
  replacingComponentDesignId?: number;
};

export function postCreateTddInJob(
  analysisReportGuid: string,
  jobId: number,
  replacingComponentDesignId?: number,
): Promise<SealingServiceComponentDesign> {
  const data: CreateTddInJobRequest = { jobId, replacingComponentDesignId };
  return DesignService.post(`componentDesigns/${analysisReportGuid}/createTddInJob`, { data });
}

export function downloadShopDrawings(downloadShopDrawingsRequest: DownloadShopDrawingsRequest) {
  return DesignService.fetchFile('public/componentDesigns/download/shopDrawings', {
    post: true,
    mightEmail: downloadShopDrawingsRequest.allowEmail,
    data: downloadShopDrawingsRequest,
  });
}

export function getDxf(analysisReportGuid: string) {
  return DesignService.fetchFile(`componentDesigns/${analysisReportGuid}/Dxf`);
}

export function setMaterialSelection(
  componentDesignGuid: string,
  selection: MaterialSelection,
): Promise<ComponentDesignTimestamps> {
  return DesignService.put(`componentDesigns/${componentDesignGuid}/materialSelection`, { data: selection });
}

export function getComponentDesignSummariesAndTimestamps(
  componentDesignGuids: readonly string[],
): Promise<ComponentDesignSummariesAndTimestamps> {
  return DesignService.post('componentDesigns/summariesAndTimestamps', { data: componentDesignGuids });
}

export function downloadCanaryCsv(excavations: readonly Excavation[]) {
  return DesignService.fetchFile(`convertUtilities/canaryCollection`, { post: true, data: excavations });
}

export function getComponentDesignTemplateSummaries(): Promise<ComponentDesignSummary[]> {
  return DesignService.get('componentDesigns/templates');
}

export const allLockTargets = ['analog', 'hangers', 'loading', 'members', 'plating'] as const;
export type LockTarget = (typeof allLockTargets)[number];

type ComponentDesignsLockActionRequest = {
  lockTargets: readonly LockTarget[];
  value: boolean;
  componentDesignGuids: string[];
};

export function componentDesignsBatchLockAction(
  data: ComponentDesignsLockActionRequest,
): Promise<ComponentDesignSummariesAndTimestamps> {
  return DesignService.post('componentDesigns/lockAction', { data });
}

interface CreateJobRequestBase {
  readonly jobType: 'Seal' | 'Repair';

  readonly trussDesignDrawingTemplateId: number | null;
  readonly trussDesignDrawingOverflowTemplateId: number | null;
  readonly serializedJobForUpload: string;
  readonly sourceProjectGuid: string;
  readonly name: string;
  readonly shareWithOrganization: string | null;
  readonly sendEmailForAnalysisErrorsOrOverCapacity: boolean;
}

export interface CreateJobRequest extends CreateJobRequestBase {
  readonly componentDesignGuids: string[];
  readonly componentDesignQuantities: { [guid: string]: number };
}

export interface CreateJobFromExistingComponentSetRequest extends CreateJobRequestBase {
  readonly sharedComponentSetLookupCode: string;
}

export function postCreateJob(data: CreateJobRequest) {
  return DesignService.post<number>('componentDesigns/createJob', { data });
}

export function postCreateJobFromExistingComponentSet(data: CreateJobFromExistingComponentSetRequest) {
  return DesignService.post<number>('componentDesigns/createJobFromExistingComponentSet', { data });
}

export function getMultiPlyConnectionDetailNote(componentDesignGuid: string): Promise<string> {
  return DesignService.get(`componentDesigns/${componentDesignGuid}/getMultiPlyConnectionDetailNote`);
}

export function postDeleteMembers(componentDesign: ComponentDesign, memberGuids: string[]): Promise<ComponentDesign> {
  return DesignService.post('componentDesigns/deleteMembers', { data: { componentDesign, memberGuids } });
}

export function postDeleteBearing(componentDesign: ComponentDesign, bearingGuid: string): Promise<ComponentDesign> {
  return DesignService.post(`componentDesigns/deleteBearing/${bearingGuid}`, { data: componentDesign });
}

interface SpliceRequest {
  componentDesign: ComponentDesign;
  chordGuid: string;
  distanceFromLeft: number;
}

export function postAddSplices(spliceRequest: SpliceRequest): Promise<ComponentDesign> {
  return DesignService.post('componentDesigns/addSplices', { data: spliceRequest });
}

export function postDeleteSplice(
  componentDesign: ComponentDesign,
  neighbor1Guid: string,
  neighbor2Guid: string,
): Promise<ComponentDesign> {
  return DesignService.post(`componentDesigns/deleteSplice/${neighbor1Guid}/${neighbor2Guid}`, {
    data: componentDesign,
  });
}

export function postDeleteAllSplices(componentDesign: ComponentDesign): Promise<ComponentDesign> {
  return DesignService.post(`componentDesigns/deleteAllSplices`, {
    data: componentDesign,
  });
}

export function postAutoSplice(componentDesign: ComponentDesign): Promise<ComponentDesign> {
  return DesignService.post('componentDesigns/autoSplice', { data: componentDesign });
}

interface DeleteHangersResponse {
  readonly updatedComponentDesign: ComponentDesign;
  readonly jackGirderConnections: readonly JackGirderConnection[];
}

export function deleteHangers(
  componentDesign: ComponentDesign,
  guidsOfHangersToDelete: readonly string[],
): Promise<DeleteHangersResponse> {
  return DesignService.delete('componentDesigns/hangers', { data: { componentDesign, guidsOfHangersToDelete } });
}

interface SelectHangersResponse {
  readonly changes: SelectHangersResponseChanges | null;
}

interface SelectHangersResponseChanges {
  readonly updatedComponentDesign: ComponentDesign;
  readonly jackGirderConnections: readonly JackGirderConnection[];
}

export function postSelectHangers(componentDesign: ComponentDesign): Promise<SelectHangersResponse> {
  return DesignService.post('componentDesigns/selectHangers', { data: componentDesign });
}

interface ChangeHangerResponse {
  readonly updatedComponentDesign: ComponentDesign;
  readonly jackGirderConnections: readonly JackGirderConnection[];
}

export function postChangeHangers(
  componentDesign: ComponentDesign,
  hangerGuid: string,
  newConnector: ConnectingHardware,
): Promise<ChangeHangerResponse> {
  return DesignService.post('componentDesigns/changeHanger', { data: { componentDesign, hangerGuid, newConnector } });
}

export interface UpdateResponse {
  dimensionSet: DimensionSet | null;
  componentDesignWithTimestamps: ComponentDesignWithTimestamps;
  successfullySynchronized: boolean | null;
  errorUpdatingAssemblyNames: boolean;
  errorUpdatingDimensions: boolean;
}

export function postUpdate(
  componentDesign: ComponentDesign,
  lastUpdatedTimestampOverride?: string,
): Promise<UpdateResponse> {
  const params = new URLSearchParams();
  if (lastUpdatedTimestampOverride) {
    params.append('lastUpdatedTimestampOverride', lastUpdatedTimestampOverride);
  }

  return DesignService.post('componentDesigns/update', { params, data: componentDesign });
}

export function postDropTopChords(
  componentDesign: ComponentDesign,
  chordGuids: readonly string[],
  angle: number,
  thickness: number,
  fullyCrossing: boolean,
): Promise<ComponentDesign> {
  return DesignService.post(`componentDesigns/dropTopChords`, {
    data: { componentDesign, chordGuids, angle, thickness, fullyCrossing },
  });
}

export function postStackChord(
  componentDesign: ComponentDesign,
  memberGuid: string,
  startLocation: number,
  endLocation: number,
): Promise<ComponentDesign> {
  return DesignService.post(`componentDesigns/stackChord/${memberGuid}/${startLocation}/${endLocation}`, {
    data: componentDesign,
  });
}

export function postStub(
  componentDesign: ComponentDesign,
  componentSide: ComponentSide,
  distanceStubOption: StubDistanceOption,
  distance: number,
): Promise<ComponentDesign> {
  return DesignService.post(`componentDesigns/stub/${componentSide}/${distanceStubOption}/${distance}`, {
    data: componentDesign,
  });
}

export function postGable(
  componentDesign: ComponentDesign,
  gableSettings: GableSettings,
  gableStudLumber: Lumber,
): Promise<ComponentDesign> {
  return DesignService.post(`componentDesigns/gable`, {
    data: { transferComponentDesign: componentDesign, gableSettings, gableStudLumber },
  });
}

export function postUnstackChord(componentDesign: ComponentDesign, memberGuid: string): Promise<ComponentDesign> {
  return DesignService.post(`componentDesigns/unstackChord/${memberGuid}`, { data: componentDesign });
}

export function postStackWeb(componentDesign: ComponentDesign, memberGuid: string): Promise<ComponentDesign> {
  return DesignService.post(`componentDesigns/stackWeb/${memberGuid}`, { data: componentDesign });
}

export function postUnstackWeb(componentDesign: ComponentDesign, memberGuid: string): Promise<ComponentDesign> {
  return DesignService.post(`componentDesigns/unstackWeb/${memberGuid}`, { data: componentDesign });
}

export function postReverseWeb(componentDesign: ComponentDesign, memberGuid: string): Promise<ComponentDesign> {
  return DesignService.post(`componentDesigns/reverseWeb/${memberGuid}`, { data: componentDesign });
}

export function postMakeWebVertical(
  componentDesign: ComponentDesign,
  memberGuid: string,
  adjustLowerEnd: boolean,
): Promise<ComponentDesign> {
  return DesignService.post(`componentDesigns/makeWebVertical/${memberGuid}/${adjustLowerEnd}`, {
    data: componentDesign,
  });
}

export function postAdjustCut(
  componentDesign: ComponentDesign,
  memberGuid: string,
  adjustLowerEnd: boolean,
  shiftClockwise: boolean,
): Promise<ComponentDesign> {
  return DesignService.post(`componentDesigns/adjustCut/${memberGuid}/${adjustLowerEnd}/${shiftClockwise}`, {
    data: componentDesign,
  });
}

interface DefineFeaturesResponse {
  zones: ReadonlyArray<Zone>;
}

export function postDefineFeatures(componentDesign: ComponentDesign): Promise<DefineFeaturesResponse> {
  return DesignService.post('componentDesigns/defineFeatures', { data: componentDesign });
}

export function postGenerateProfileFromComponentDesign(componentDesign: ComponentDesign): Promise<ComponentDesign> {
  return DesignService.post(`componentDesigns/generateProfileFromComponent`, { data: componentDesign });
}

export interface CreateFeaturePatternTrussRequest {
  readonly featurePatternTrussSettings: FeaturePatternTrussSettings;
  readonly materialSettings: MaterialSettings;
  readonly organization: string;
  readonly materialSelection: MaterialSelection;
  readonly memberAnalysisInputs: MemberAnalysisInputs | null;
}

export function postCreateAndInsertFeaturePatternTruss(
  request: CreateFeaturePatternTrussRequest,
  projectGuidForUsageLogging?: string,
) {
  return DesignService.post<CreateAndInsertTrussResponse>('componentDesigns/createAndInsertFeaturePatternTruss', {
    data: request,
    params: projectGuidForUsageLogging ? new URLSearchParams({ projectGuidForUsageLogging }) : undefined,
  });
}

interface CreateFeaturePatternTrussPreviewResponse {
  readonly componentDesign: ComponentDesign;
  readonly memberAnalysisInfo: MemberAnalysisInfo | null;
}

export function postCreateFeaturePatternTrussPreview(request: CreateFeaturePatternTrussRequest) {
  return DesignService.post<CreateFeaturePatternTrussPreviewResponse>(
    `componentDesigns/createFeaturePatternTrussPreview`,
    {
      data: request,
    },
  );
}

interface RegenerateFeaturePatternTrussFromSettingsRequest {
  readonly createFeaturePatternTrussRequest: CreateFeaturePatternTrussRequest;
  readonly componentDesign: ComponentDesign;
  readonly girderConnections: readonly JackGirderConnection[];
}

export interface RegenerateTrussFromSettingsResponse {
  readonly componentDesign: ComponentDesign;
  readonly timestamps: ComponentDesignTimestamps[];
  readonly updatedGirderConnections: JackGirderConnection[];
  readonly dimensionSet: DimensionSet;
}

export function regenerateFeaturePatternTrussFromSettings(request: RegenerateFeaturePatternTrussFromSettingsRequest) {
  return DesignService.post<RegenerateTrussFromSettingsResponse>(
    'componentDesigns/regenerateFeaturePatternTrussFromSettings',
    {
      data: request,
    },
  );
}

export function getDefaultProfileTrussSettings(): Promise<ProfileTrussSettings> {
  return DesignService.get(`componentDesigns/defaultProfileTrussSettings`);
}

export function getDefaultFeaturePatternTrussSettings(
  templateType: ComponentTemplateType,
): Promise<FeaturePatternTrussSettings> {
  return DesignService.get(`componentDesigns/defaultFeaturePatternTrussSettings`, {
    params: new URLSearchParams({ template: templateType }),
  });
}

interface CreateAndInsertTrussResponse {
  readonly componentDesign: ComponentDesign;
  readonly timestamps: ComponentDesignTimestamps;
  readonly memberAnalysisInfo: MemberAnalysisInfo | null;
}

export interface CreateProfileTrussRequest {
  readonly profileTrussSettings: ProfileTrussSettings;
  readonly materialSettings: MaterialSettings;
  readonly scarfCutThreshold: number;
  readonly organization: string;
  readonly materialSelection: MaterialSelection;
  readonly memberAnalysisInputs: MemberAnalysisInputs | null;
  readonly webbingInfo: WebbingInfo | null;
}

export function postCreateAndInsertProfileTruss(
  request: CreateProfileTrussRequest,
  projectGuidForUsageLogging?: string,
) {
  return DesignService.post<CreateAndInsertTrussResponse>(`componentDesigns/createAndInsertProfileTruss`, {
    data: request,
    params: projectGuidForUsageLogging ? new URLSearchParams({ projectGuidForUsageLogging }) : undefined,
  });
}

interface CreateProfileTrussPreviewResponse {
  readonly componentDesign: ComponentDesign;
  readonly webbingInfo: WebbingInfo | null;
  readonly memberAnalysisInfo: MemberAnalysisInfo | null;
}

export function postCreateProfileTrussPreview(request: CreateProfileTrussRequest) {
  return DesignService.post<CreateProfileTrussPreviewResponse>(`componentDesigns/createProfileTrussPreview`, {
    data: request,
  });
}

interface RegenerateProfileTrussFromSettingsRequest {
  readonly createProfileTrussRequest: CreateProfileTrussRequest;
  readonly componentDesign: ComponentDesign;
  readonly girderConnections: readonly JackGirderConnection[];
}

export function regenerateProfileTrussFromSettings(request: RegenerateProfileTrussFromSettingsRequest) {
  return DesignService.post<RegenerateTrussFromSettingsResponse>(
    'componentDesigns/regenerateProfileTrussFromSettings',
    {
      data: request,
    },
  );
}

export function postEditEndCondition(
  componentDesign: ComponentDesign,
  endConditionSettings: EndConditionSettings,
  side: ComponentSide,
  materialSettings: MaterialSettings | null,
): Promise<ComponentDesign> {
  return DesignService.post('componentDesigns/editEndCondition', {
    data: { componentDesign, endConditionSettings, side, materialSettings },
  });
}

export function postDuplicateComponentDesign(
  componentDesignGuid: string,
  options: { componentName: string; duplicateSettings: boolean; duplicateJackConnections: boolean },
  projectGuidForUsageLogging?: string,
): Promise<ComponentDesignWithTimestamps> {
  const params = new URLSearchParams({
    name: options.componentName,
    duplicateSettings: options.duplicateSettings.toString(),
    duplicateJackConnections: options.duplicateJackConnections.toString(),
  });
  if (projectGuidForUsageLogging) params.append('projectGuidForUsageLogging', projectGuidForUsageLogging);

  return DesignService.post(`componentDesigns/${componentDesignGuid}/duplicate`, { params });
}

export function postDuplicateMultipleComponentDesignsWithoutSettings(
  targetProjectGuid: string,
  componentDesignGuids: string[],
  materialSelection?: MaterialSelection,
): Promise<ComponentDesignWithTimestamps[]> {
  return DesignService.post(`componentDesigns/duplicateMultipleWithoutSettings`, {
    data: { targetProjectGuid, componentDesignGuids, materialSelection },
  });
}

type ReplaceLumberResponse = { didReplace: true; componentDesign: ComponentDesign } | { didReplace: false };

export function postReplaceLumber(request: ReplaceLumberRequest): Promise<ReplaceLumberResponse> {
  return DesignService.post(`componentDesigns/replaceLumber`, { data: request });
}

export function postCreateGusset(
  componentDesignGuid: string,
  clickedPoint: Point2D,
  height: number,
  width: number,
  material: string,
) {
  return DesignService.post<ComponentDesign>(`componentDesigns/createGusset`, {
    data: { componentDesignGuid, clickedPoint, height, width, material },
  });
}

////////////////////
// Download files //
////////////////////

export function downloadBlade(downloadRequest: DownloadRequest) {
  return DesignService.fetchFile(`componentDesigns/download/blade`, {
    data: downloadRequest,
    post: true,
  });
}

export function downloadBvn(downloadRequest: DownloadRequest) {
  return DesignService.fetchFile(`public/componentDesigns/download/bvn`, {
    data: downloadRequest,
    post: true,
  });
}

export function downloadCyb(downloadRequest: DownloadRequest, memberGuid?: string) {
  return DesignService.fetchFile(`componentDesigns/download/cyb`, {
    data: downloadRequest,
    post: true,
    params: memberGuid ? new URLSearchParams({ memberGuid }) : undefined,
  });
}

export function downloadComponentDesignIfc(downloadRequest: DownloadRequest): Promise<DownloadableFileResponse> {
  return DesignService.fetchFile('public/componentDesigns/download/ifc', {
    post: true,
    data: downloadRequest,
  });
}

export function downloadPcd(downloadRequest: DownloadRequest) {
  return DesignService.fetchFile(`componentDesigns/download/pcd`, {
    post: true,
    data: downloadRequest,
  });
}

export function downloadTre(downloadRequest: DownloadRequest) {
  return DesignService.fetchFile('public/componentDesigns/download/tre', {
    data: downloadRequest,
    post: true,
  });
}

export function downloadTps(downloadRequest: DownloadRequest) {
  return DesignService.fetchFile(`public/componentDesigns/download/tps`, {
    data: downloadRequest,
    post: true,
  });
}

export function downloadTrs(downloadRequest: DownloadRequest) {
  return DesignService.fetchFile('componentDesigns/download/trs', {
    data: downloadRequest,
    post: true,
  });
}

export function downloadSteelTrs(downloadRequest: DownloadTrussDAndERequest) {
  return DesignService.fetchFile('componentDesigns/download/steelTrs', {
    data: downloadRequest,
    post: true,
  });
}

export function downloadTrussDAndE(downloadRequest: DownloadTrussDAndERequest) {
  return DesignService.fetchFile('componentDesigns/download/trussDAndE', {
    data: downloadRequest,
    post: true,
  });
}

export function downloadMachineryTrs(downloadRequest: DownloadMachineryTrsRequest) {
  return DesignService.fetchFile('public/componentDesigns/download/machineryTrs', {
    data: downloadRequest,
    post: true,
    potentiallyNoFileContent: true,
  });
}

export function downloadOmn(downloadRequest: DownloadRequest) {
  return DesignService.fetchFile('public/componentDesigns/download/omn', {
    data: downloadRequest,
    post: true,
  });
}

//////////////////////
// Report downloads //
//////////////////////

export function downloadComponentReport(downloadRequest: DownloadRequest) {
  return DesignService.fetchFile('public/componentDesigns/download/componentReport', {
    post: true,
    data: downloadRequest,
  });
}

export function downloadLumberPickList(downloadRequest: DownloadRequest) {
  return DesignService.fetchFile(`public/componentDesigns/download/lumberPickList`, {
    post: true,
    data: downloadRequest,
  });
}

export function downloadMaterialReportCsv(downloadMaterialReportRequest: DownloadMaterialReportRequest) {
  return DesignService.fetchFile(`public/componentDesigns/download/materialReportCsv`, {
    post: true,
    data: downloadMaterialReportRequest,
  });
}

export function downloadMaterialReportPdf(downloadMaterialReportRequest: DownloadMaterialReportRequest) {
  return DesignService.fetchFile(`public/componentDesigns/download/materialReportPdf`, {
    post: true,
    data: downloadMaterialReportRequest,
  });
}

export function downloadPlatePickList(downloadRequest: DownloadRequest) {
  return DesignService.fetchFile(`public/componentDesigns/download/platePickList`, {
    post: true,
    data: downloadRequest,
  });
}

export function comparePlateLists(request: ComparePlateListsRequest) {
  return DesignService.fetchFile(`compareutilities/comparePlateLists`, {
    post: true,
    data: request,
  });
}
